import { useState } from "react";

export type TWebsiteBuilderLoadingStatus = "loading" | "failed" | "succeeded";

interface IWebsiteBuilderLoadingState<T> {
  state: T;
  stateExists: boolean;
  stateLoadingStatus: TWebsiteBuilderLoadingStatus;
}

interface IUseWebsiteBuilderLoadingState<T> {
  state: T;
  stateExists: boolean;
  stateLoadingStatus: TWebsiteBuilderLoadingStatus;
  loadInProgress: () => void;
  loadFailed: () => void;
  loadSucceeded: (updatedState: T, updatedStateExists: boolean) => void;
  setState: (updatedState: T, updatedStateExists: boolean) => void;
}

export function useWebsiteBuilderLoadingState<T>(initialLoadingState: IWebsiteBuilderLoadingState<T>): IUseWebsiteBuilderLoadingState<T> {
  const [loadingState, setLoadingState] = useState<IWebsiteBuilderLoadingState<T>>(initialLoadingState);

  function loadInProgress() {
    setLoadingState(prev => ({
      ...prev,
      state: null,
      stateExists: false,
      stateLoadingStatus: "loading",
    }));
  }

  function loadFailed() {
    setLoadingState(prev => ({
      ...prev,
      state: null,
      stateExists: false,
      stateLoadingStatus: "failed",
    }));
  }

  function loadSucceeded(updatedState: T, updatedStateExists: boolean) {
    setLoadingState(prev => ({
      ...prev,
      state: updatedState,
      stateExists: updatedStateExists,
      stateLoadingStatus: "succeeded",
    }));
  }

  function setState(updatedState: T, updatedStateExists: boolean) {
    setLoadingState(prev => ({
      ...prev,
      state: updatedState,
      stateExists: updatedStateExists,
    }));
  }

  return {
    state: loadingState.state,
    stateExists: loadingState.stateExists,
    stateLoadingStatus: loadingState.stateLoadingStatus,
    loadInProgress,
    loadFailed,
    loadSucceeded,
    setState,
  };
}